import { MOTION } from '../../lib/motion.ts'

export type LoaderSize = 'sm' | 'md' | 'lg'
export type LoaderGround = 'void' | 'bone'

// RAL-182 direction 1a "Emission": the pulse leaves the oxide centre bar and reaches the outer
// pair 320ms later. Fixed per-index delays, not staggerChildren — the pattern is mirror-symmetric,
// and a stagger would sweep it left-to-right (rejected direction 1b) and imply a travel direction.
const EMISSION_DELAY = [0.32, 0.16, 0, 0.16, 0.32] as const
const EMISSION_DURATION = 1.4
// MOTION.ease.out is a readonly tuple of literals; motion's Transition wants a mutable
// four-number cubic bezier.
const EMISSION_EASE = [...MOTION.ease.out] as [number, number, number, number]

// The loop is specified at lg and does not survive scaling down unchanged: at a 20px mark a
// scaleY: 0.5 pulse moves the outer bar 3.5px, which reads as jitter and shimmers sub-pixel rect
// edges. sm switches channel to opacity with the geometry frozen; md keeps scale but floors it at
// 0.65 so the canonical height ratio still reads as the Rally mark. Table keeps size and motion
// paired so no caller can pick a mismatched combination.
export const LOADER_SIZE = {
  sm: {
    markHeight: 20,
    channel: 'opacity',
    // An opacity rise reads faster than a scale rise at the same duration, so the keyframe opens
    // slightly later to keep the same perceived rise:fall asymmetry.
    floor: 0.25,
    times: [0, 0.2, 1],
    // Outer bars sit muted at sm: at full ink they overpower the readout row they replace.
    ink: 'var(--color-ink-meta)',
    labelClass: 'text-[10px]',
    layout: 'inline'
  },
  md: {
    markHeight: 42,
    channel: 'scaleY',
    floor: 0.65,
    times: [0, 0.16, 1],
    ink: 'var(--color-ink-primary)',
    labelClass: 'text-[11px]',
    layout: 'stacked'
  },
  lg: {
    markHeight: 112,
    channel: 'scaleY',
    floor: 0.5,
    times: [0, 0.16, 1],
    ink: 'var(--color-ink-primary)',
    labelClass: 'text-xs',
    layout: 'stacked'
  }
} as const

// Exported as the single source for what each bar does, so the size/channel pairing is assertable
// without reaching into rendered motion state.
export const emissionAnimation = (size: LoaderSize, index: number) => {
  const spec = LOADER_SIZE[size]
  const keyframes = [spec.floor, 1, spec.floor]

  return {
    animate: spec.channel === 'opacity' ? { opacity: keyframes } : { scaleY: keyframes },
    transition: {
      duration: EMISSION_DURATION,
      delay: EMISSION_DELAY[index],
      // Fast rise, slow fall. An even split reads as a heartbeat rather than a transmission;
      // the asymmetry is the whole character of the loop.
      times: [...spec.times],
      ease: EMISSION_EASE,
      repeat: Infinity,
      repeatDelay: 0
    }
  }
}

// The indeterminate rule runs slower at boot than on a route change: a full-viewport sweep
// covers more distance, and matching the route-change speed there reads as hurried.
export const ROUTE_RULE_DURATION = 1.4
export const BOOT_RULE_DURATION = 1.9
